/**
 * The staff: engraving, hit-testing, and every route into writing a note.
 *
 * Three input routes converge on the same two actions (write a pitch into a
 * measure, or clear it back to a rest):
 *
 *   pointer — press anywhere in a column to see the notes allowed there, slide
 *             to choose, release to write. Releasing on the note already there
 *             clears it. That single gesture is a click on a mouse and a
 *             press-and-hold on a touchscreen, with no separate code path.
 *   keyboard — arrows move a caret, Enter writes, Delete clears.
 *   nothing at all — the toolbar and the on-screen controls cover every action,
 *             so a device with neither hover nor a keyboard is fully served.
 *
 * Judgment stays hidden until the student presses Grade. Before then the only
 * red on the staff is the dead-end outline, which is an alarm about a corner
 * painted, not a verdict on a note written.
 */

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  analyze,
  clampToWindow,
  diatonicDistance,
  edoOf,
  type Analysis,
  measureCount,
  nameOfPitch,
  notatedAccidentalGlyph,
  ordinal,
  pitchAtStaffPosition,
  signatureGlyph,
  simpleNumber,
  staffPositionOf,
  type Exercise,
} from '../../core';
import { useSession } from '../../state/store';
import { usePlayback, auditionCantus } from '../../state/playback';
import {
  barlineX,
  computeLayout,
  collisionOffset,
  collisionOffsetAtStaffPosition,
  dAt,
  measureAt,
  signatureMarks,
  SPACE,
  xOf,
  type ScoreLayout,
} from './layout';
import { Accidental, Clef, PitchCaret, StaffLines, WholeNote, WholeRest } from './glyphs';

/** What the pointer is over. */
type Hit =
  | { kind: 'write'; measure: number; pitch: number }
  | { kind: 'cantus'; measure: number }
  | { kind: 'rail'; measure: number };

/** Motion ribbon colours, keyed by motion type. */
const MOTION_COLOR: Record<string, string> = {
  c: 'var(--motion-contrary)',
  o: 'var(--motion-oblique)',
  s: 'var(--motion-similar)',
  p: 'var(--danger)',
};

export function ScoreView({ onEditKey }: { onEditKey: () => void }): JSX.Element {
  const exercise = useSession((s) => s.exercise);
  const settings = useSession((s) => s.settings);
  const legality = useSession((s) => s.legality);
  const report = useSession((s) => s.report);
  const cursor = useSession((s) => s.cursor);
  const keyboardMode = useSession((s) => s.keyboardMode);
  const toggleNote = useSession((s) => s.toggleNote);
  const eraseNote = useSession((s) => s.eraseNote);
  const setCursor = useSession((s) => s.setCursor);
  const setHoverMeasure = useSession((s) => s.setHoverMeasure);
  const setKeyboardMode = useSession((s) => s.setKeyboardMode);
  const moveCursor = useSession((s) => s.moveCursor);
  const moveCursorPitch = useSession((s) => s.moveCursorPitch);
  const commitCursor = useSession((s) => s.commitCursor);

  const status = usePlayback((s) => s.status);
  const playhead = usePlayback((s) => s.playhead);
  const seek = usePlayback((s) => s.seek);
  const playStart = useSession((s) => s.playStart);

  const svgRef = useRef<SVGSVGElement>(null);
  const [pointer, setPointer] = useState<Hit | null>(null);
  const [focused, setFocused] = useState(false);
  const pressing = useRef(false);

  const n = measureCount(exercise);

  // The written music sets where the interval rail sits. The caret and every
  // legal candidate are only *reserved*: they stretch the frame so the staff
  // never jumps as ghosts and the cursor come and go, but they do not push the
  // rail away from the staff it belongs under.
  const reserved = useMemo(() => {
    const out: number[] = [];
    for (const candidates of legality.allowed) for (const c of candidates) out.push(c.d);
    return out;
  }, [legality]);

  const layout = useMemo(
    () => computeLayout(exercise, settings.layout, [], [...reserved, cursor.pitch]),
    [exercise, settings.layout, cursor.pitch, reserved],
  );

  const analysis = useMemo(
    () => report?.analysis ?? analyze(exercise, settings.profile),
    [exercise, settings.profile, report],
  );

  /* ---- hit-testing ------------------------------------------------------ */

  const hitTest = useCallback(
    (clientX: number, clientY: number): Hit | null => {
      const svg = svgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * layout.width;
      const y = ((clientY - rect.top) / rect.height) * layout.height + layout.viewTop;
      const measure = measureAt(x, layout);
      if (measure == null) return null;

      // The rail is a narrow band; it seeks, so it is checked first.
      if (Math.abs(y - layout.railY) <= 13) return { kind: 'rail', measure };

      if (!layout.single) {
        const dCp = Math.abs(y - (layout.cpStaff.top + 2 * SPACE));
        const dCf = Math.abs(y - (layout.cfStaff.top + 2 * SPACE));
        if (dCf < dCp) return { kind: 'cantus', measure };
      }

      const staffPosition = dAt(y, layout.cpStaff);
      const pitch = clampToWindow(
        exercise,
        measure,
        pitchAtStaffPosition(staffPosition, measure, exercise),
      );
      return { kind: 'write', measure, pitch };
    },
    [layout, exercise],
  );

  const samePlace = (a: Hit | null, b: Hit | null): boolean =>
    a === b ||
    (!!a &&
      !!b &&
      a.kind === b.kind &&
      a.measure === b.measure &&
      (a.kind !== 'write' || (b as any).pitch === a.pitch));

  const updatePointer = useCallback(
    (hit: Hit | null) => {
      setPointer((prev) => (samePlace(prev, hit) ? prev : hit));
      setHoverMeasure(hit?.measure ?? null);
      // Whichever input moved last owns the highlight, so a stale hover never
      // sits on top of the caret the student is actually steering.
      if (hit) setKeyboardMode(false);
    },
    [setHoverMeasure, setKeyboardMode],
  );

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    const hit = hitTest(e.clientX, e.clientY);
    if (!hit) return;
    // Right-click lifts a note outright — one of the three erase routes.
    if (e.button === 2) {
      eraseNote(hit.measure);
      return;
    }
    pressing.current = true;
    try {
      // Capture keeps a finger that slides off the staff still steering the
      // note it started on. Some environments refuse it; the gesture works
      // either way, so a refusal is not worth failing the interaction over.
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture unavailable */
    }
    updatePointer(hit);
    if (hit.kind === 'write') setCursor(hit.measure, hit.pitch);
  };

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!pressing.current && e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    updatePointer(hitTest(e.clientX, e.clientY));
  };

  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!pressing.current) return;
    pressing.current = false;
    const hit = hitTest(e.clientX, e.clientY);
    if (hit) {
      if (hit.kind === 'write') toggleNote(hit.measure, hit.pitch);
      else if (hit.kind === 'cantus') auditionCantus(hit.measure);
      else seek(hit.measure);
    }
    // A touch has no hover, so the ghosts leave with the finger.
    if (e.pointerType === 'touch') updatePointer(null);
  };

  const onPointerLeave = () => {
    if (pressing.current) return;
    updatePointer(null);
  };

  /* ---- keyboard --------------------------------------------------------- */

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        moveCursor(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        moveCursor(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveCursorPitch(1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveCursorPitch(-1);
        break;
      case 'Home':
        e.preventDefault();
        setCursor(0);
        break;
      case 'End':
        e.preventDefault();
        setCursor(n - 1);
        break;
      case 'Enter':
        e.preventDefault();
        commitCursor();
        break;
      case 'Backspace':
      case 'Delete':
        e.preventDefault();
        eraseNote(cursor.measure);
        break;
      default:
        return;
    }
    e.stopPropagation();
  };

  /* ---- what is highlighted right now ------------------------------------ */

  const graded = report != null;
  const steering = keyboardMode && focused;
  const activeMeasure = steering ? cursor.measure : (pointer?.measure ?? null);
  const previewPitch = steering ? cursor.pitch : pointer?.kind === 'write' ? pointer.pitch : null;
  const previewMeasure = steering ? cursor.measure : pointer?.kind === 'write' ? pointer.measure : null;

  const ink = 'var(--score-ink)';
  const cfColor = layout.single ? 'var(--cantus)' : ink;
  const cpColor = 'var(--score-user)';

  /* ---- pieces ----------------------------------------------------------- */

  const badMeasures = useMemo(
    () => (graded ? new Set(analysis.findings.filter((f) => f.severity === 'error').map((f) => f.measure)) : new Set<number>()),
    [graded, analysis],
  );

  const ghosts = useMemo(() => {
    if (!settings.assists.allowed || activeMeasure == null) return [];
    return (legality.allowed[activeMeasure] ?? []).filter((c) => c.d !== exercise.cp[activeMeasure]);
  }, [settings.assists.allowed, activeMeasure, legality, exercise]);

  const barTo = layout.left + n * layout.measureWidth;

  return (
    <div
      className="score"
      tabIndex={0}
      role="application"
      aria-label={`Counterpoint staff in ${edoOf(exercise)}-EDO, ${n} measures. Arrow keys move the cursor, Enter writes a note, Delete clears it.`}
      onKeyDown={onKeyDown}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <div className="score__scroll">
        <svg
          ref={svgRef}
          className="score__svg"
          width={layout.width}
          height={layout.height}
          viewBox={`0 ${layout.viewTop} ${layout.width} ${layout.height}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            pressing.current = false;
            updatePointer(null);
          }}
          onPointerLeave={onPointerLeave}
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* flagged measures — only ever after grading */}
          {[...badMeasures].map((m) => (
            <rect
              key={`bad${m}`}
              className="score__flag"
              x={barlineX(m, layout) + 3}
              y={layout.columnTop}
              width={layout.measureWidth - 6}
              height={layout.columnHeight}
              rx={8}
            />
          ))}

          {/* the column under the pointer or caret */}
          {activeMeasure != null && (
            <rect
              className="score__column"
              x={barlineX(activeMeasure, layout) + 2}
              y={layout.columnTop}
              width={layout.measureWidth - 4}
              height={layout.columnHeight}
              rx={8}
            />
          )}

          {/* dead ends: a measure with no legal note left */}
          {settings.assists.allowed &&
            Object.keys(legality.deadEnds).map((k) => {
              const m = Number(k);
              return (
                <rect
                  key={`dead${m}`}
                  className="score__deadend"
                  x={barlineX(m, layout) + 3}
                  y={layout.columnTop}
                  width={layout.measureWidth - 6}
                  height={layout.columnHeight}
                  rx={8}
                />
              );
            })}

          {/* staves, barlines, clefs, key signature */}
          {layout.staves.map((staff, i) => (
            <StaffLines key={i} staff={staff} from={6} to={barTo + 6} color={ink} />
          ))}
          <line
            x1={6}
            y1={layout.staves[0].top}
            x2={6}
            y2={layout.staves[layout.staves.length - 1].top + 4 * SPACE}
            stroke={ink}
            strokeWidth={2.5}
          />
          {Array.from({ length: n + 1 }, (_, i) => i).map((i) =>
            layout.staves.map((staff, s) => (
              <line
                key={`bar${i}-${s}`}
                x1={barlineX(i, layout)}
                y1={staff.top}
                x2={barlineX(i, layout)}
                y2={staff.top + 4 * SPACE}
                stroke={ink}
                strokeWidth={i === n ? 1 : 0.8}
                opacity={i === n ? 1 : 0.5}
              />
            )),
          )}
          {layout.staves.map((staff, s) => (
            <line
              key={`final${s}`}
              x1={barTo + 5}
              y1={staff.top}
              x2={barTo + 5}
              y2={staff.top + 4 * SPACE}
              stroke={ink}
              strokeWidth={3.5}
            />
          ))}
          {layout.staves.map((staff, i) => (
            <Clef key={`clef${i}`} staff={staff} color={ink} />
          ))}
          {/* The clef and key signature are a control: tapping them opens the
              key chooser, which is where an exercise's identity is decided. */}
          <g
            className="score__keyzone"
            role="button"
            tabIndex={0}
            aria-label={`Tuning: ${edoOf(exercise)}-EDO. Key: ${exercise.key.label}. Activate to change tuning, key, and voicing.`}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => {
              e.stopPropagation();
              onEditKey();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onEditKey();
              }
            }}
          >
            <rect
              x={2}
              y={layout.staves[0].top - 12}
              width={layout.left - 8}
              height={layout.staves[layout.staves.length - 1].top + 4 * SPACE + 12 - (layout.staves[0].top - 12)}
              rx={8}
            />
          </g>

          {layout.staves.flatMap((staff, i) =>
            signatureMarks(exercise.key, staff).map((mark, j) => (
              <text
                key={`sig${i}-${j}`}
                x={mark.x}
                y={mark.y}
                fontSize={20}
                fill={ink}
                fontFamily="serif"
                pointerEvents="none"
              >
                {signatureGlyph(exercise.key.sig)}
              </text>
            )),
          )}

          {/* the given melody */}
          {exercise.cf.map((d, i) => (
            <CantusNote key={`cf${i}`} ex={exercise} layout={layout} measure={i} d={d} color={cfColor} />
          ))}

          {/* allowed-note ghosts in the active column */}
          {ghosts.map((c) => (
            <EngravedNote
              key={`ghost${c.d}`}
              ex={exercise}
              measure={activeMeasure!}
              pitch={c.d}
              x={xOf(activeMeasure!, layout) + collisionOffset(exercise, layout, activeMeasure!, c.d)}
              staff={layout.cpStaff}
              color={cpColor}
              opacity={0.22}
            />
          ))}

          {/* the student's line: a note, or the rest it starts as */}
          {exercise.cp.map((d, i) =>
            d == null ? (
              <WholeRest
                key={`cp${i}`}
                x={xOf(i, layout) + collisionOffsetAtStaffPosition(exercise, layout, i, layout.restD)}
                d={layout.restD}
                staff={layout.cpStaff}
                color={cpColor}
                opacity={0.5}
              />
            ) : (
              <CounterpointNote key={`cp${i}`} ex={exercise} layout={layout} measure={i} d={d} color={cpColor} />
            ),
          )}

          {/* preview of the note about to be written */}
          {previewPitch != null && previewMeasure != null && exercise.cp[previewMeasure] !== previewPitch && (
            <EngravedNote
              ex={exercise}
              measure={previewMeasure}
              pitch={previewPitch}
              x={xOf(previewMeasure, layout) + collisionOffset(exercise, layout, previewMeasure, previewPitch)}
              staff={layout.cpStaff}
              color={cpColor}
              opacity={0.6}
            />
          )}
          {keyboardMode && focused && (
            <PitchCaret
              x={xOf(cursor.measure, layout)}
              d={staffPositionOf(cursor.pitch, edoOf(exercise))}
              staff={layout.cpStaff}
              color="var(--accent)"
            />
          )}

          {/* interval rail and motion ribbons */}
          <IntervalRail
            ex={exercise}
            layout={layout}
            analysis={analysis}
            graded={graded}
            showIntervals={settings.assists.labels}
            preview={previewPitch != null && previewMeasure != null ? { measure: previewMeasure, pitch: previewPitch } : null}
          />

          {/* playback start marker and playhead */}
          {status === 'stopped' && playStart > 0 && playStart < n && (
            <g pointerEvents="none">
              <line
                x1={barlineX(playStart, layout)}
                y1={layout.columnTop}
                x2={barlineX(playStart, layout)}
                y2={layout.columnTop + layout.columnHeight}
                stroke="var(--accent)"
                strokeWidth={2}
                strokeDasharray="5 4"
                opacity={0.6}
              />
            </g>
          )}
          {playhead != null && playhead < n && (
            <line
              className="score__playhead"
              x1={barlineX(playhead, layout) + 6}
              y1={layout.columnTop - 6}
              x2={barlineX(playhead, layout) + 6}
              y2={layout.columnTop + layout.columnHeight + 6}
              pointerEvents="none"
            />
          )}
        </svg>
      </div>

      {/* what a screen reader hears as the cursor moves */}
      <p className="sr-only" aria-live="polite">
        {describeCursor(exercise, cursor.measure, cursor.pitch, settings.assists.labels)}
      </p>
    </div>
  );
}

/* ---- note components ----------------------------------------------------- */

function CantusNote({
  ex,
  layout,
  measure,
  d,
  color,
}: {
  ex: Exercise;
  layout: ScoreLayout;
  measure: number;
  d: number;
  color: string;
}): JSX.Element {
  const x = xOf(measure, layout);
  return (
    <EngravedNote ex={ex} staff={layout.cfStaff} measure={measure} pitch={d} x={x} color={color} />
  );
}

function CounterpointNote({
  ex,
  layout,
  measure,
  d,
  color,
}: {
  ex: Exercise;
  layout: ScoreLayout;
  measure: number;
  d: number;
  color: string;
}): JSX.Element {
  const x = xOf(measure, layout) + collisionOffset(ex, layout, measure, d);
  return (
    <EngravedNote ex={ex} staff={layout.cpStaff} measure={measure} pitch={d} x={x} color={color} />
  );
}

/** A notehead and any accidental required beyond the key signature. */
function EngravedNote({
  ex,
  staff,
  measure,
  pitch,
  x,
  color,
  opacity = 1,
}: {
  ex: Exercise;
  staff: ScoreLayout['cfStaff'];
  measure: number;
  pitch: number;
  x: number;
  color: string;
  opacity?: number;
}): JSX.Element {
  const d = staffPositionOf(pitch, edoOf(ex));
  const glyph = notatedAccidentalGlyph(pitch, measure, ex);
  return (
    <>
      <WholeNote x={x} d={d} staff={staff} color={color} opacity={opacity} hollow="var(--paper)" />
      {glyph && <Accidental x={x} d={d} staff={staff} glyph={glyph} color={color} opacity={opacity} />}
    </>
  );
}

/* ---- the interval rail --------------------------------------------------- */

function IntervalRail({
  ex,
  layout,
  analysis,
  graded,
  showIntervals,
  preview,
}: {
  ex: Exercise;
  layout: ScoreLayout;
  analysis: Analysis;
  graded: boolean;
  showIntervals: boolean;
  preview: { measure: number; pitch: number } | null;
}): JSX.Element {
  const n = measureCount(ex);
  const cells: JSX.Element[] = [];

  for (let i = 0; i < n; i++) {
    const x = xOf(i, layout);
    const showingPreview = preview?.measure === i;

    if (!showIntervals) {
      // With the interval assist off the rail keeps measure numbers, so the
      // student can still say "bar 6" and aim playback — a fact, not a hint.
      cells.push(
        <text key={`num${i}`} className="rail__measure" x={x} y={layout.railY + 4} textAnchor="middle">
          {i + 1}
        </text>,
      );
      continue;
    }

    if (showingPreview) {
      const dd = ex.cfPos === 'lower'
        ? diatonicDistance(preview!.pitch, ex.cf[i], ex)
        : diatonicDistance(ex.cf[i], preview!.pitch, ex);
      cells.push(
        <text key={`prev${i}`} className="rail__preview" x={x} y={layout.railY + 4} textAnchor="middle">
          {dd >= 0 ? simpleNumber(dd) : '—'}
        </text>,
      );
      continue;
    }

    const iv = analysis.intervals[i];
    if (!iv) {
      // An em dash marks the measures still resting.
      cells.push(
        <text key={`rest${i}`} className="rail__rest" x={x} y={layout.railY + 4} textAnchor="middle">
          —
        </text>,
      );
      continue;
    }
    const bad = graded && !iv.consonant;
    cells.push(
      <text
        key={`iv${i}`}
        className={bad ? 'rail__number rail__number--bad' : 'rail__number'}
        x={x}
        y={layout.railY + 4}
        textAnchor="middle"
      >
        {iv.num}
      </text>,
    );
  }

  // Motion ribbons: a short stroke between consecutive interval numbers,
  // coloured by motion type. Parallel perfects read as similar until grading.
  const ribbons: JSX.Element[] = [];
  if (showIntervals) {
    for (let i = 1; i < n; i++) {
      let m = analysis.motions[i - 1];
      if (!m) continue;
      if (m === 'p' && !graded) m = 's';
      ribbons.push(
        <line
          key={`mo${i}`}
          x1={xOf(i - 1, layout) + 13}
          y1={layout.railY}
          x2={xOf(i, layout) - 13}
          y2={layout.railY}
          stroke={MOTION_COLOR[m]}
          strokeWidth={3}
          strokeLinecap="round"
          opacity={0.8}
        />,
      );
    }
  }

  return (
    <g pointerEvents="none">
      {ribbons}
      {cells}
    </g>
  );
}

/* ---- screen-reader narration -------------------------------------------- */

function describeCursor(ex: Exercise, measure: number, pitch: number, withInterval: boolean): string {
  const written = ex.cp[measure];
  const where = `Measure ${measure + 1} of ${measureCount(ex)}`;
  const edo = edoOf(ex);
  const content = written == null ? 'rest' : `${nameOfPitch(written, edo)}`;
  const at = `cursor on ${nameOfPitch(pitch, edo)}`;
  if (!withInterval) return `${where}, ${content}, ${at}.`;
  const dd = ex.cfPos === 'lower'
    ? diatonicDistance(pitch, ex.cf[measure], ex)
    : diatonicDistance(ex.cf[measure], pitch, ex);
  const interval = dd >= 0 ? `${ordinal(simpleNumber(dd))} above the cantus firmus` : 'below the cantus firmus';
  return `${where}, ${content}, ${at}, ${interval}.`;
}
